import { motion } from "framer-motion";
import { MapPin, Phone, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { BRANCHES, waLink, telLink } from "@/lib/site-data";

export default function Locations() {
    return (
        <section
            id="locations"
            data-testid="locations-section"
            className="relative py-24 lg:py-36 bg-cream"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14 lg:mb-20">
                    <span className="eyebrow inline-flex items-center gap-3">
                        <span className="w-8 h-px bg-gold" />
                        Visit Us
                        <span className="w-8 h-px bg-gold" />
                    </span>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-maroon tracking-tight leading-[1.05]">
                        Two warm{" "}
                        <em className="font-light text-gold-dark">Cape Town</em> locations.
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
                    {BRANCHES.map((b, i) => (
                        <motion.div
                            key={b.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, delay: i * 0.15 }}
                            className="border border-gold/30 bg-beige/30 overflow-hidden flex flex-col"
                            data-testid={`location-card-${b.id}`}
                        >
                            <div className="bg-maroon text-cream p-7 lg:p-9">
                                <div className="flex items-baseline justify-between gap-4">
                                    <h3 className="font-serif text-3xl lg:text-4xl text-gold-light tracking-tight">
                                        {b.name}
                                    </h3>
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-cream/70">
                                        Branch {i + 1}
                                    </span>
                                </div>
                                <p className="text-xs tracking-[0.18em] uppercase text-cream/60 mt-1">
                                    {b.label}
                                </p>

                                <div className="mt-6 space-y-4 text-sm">
                                    <div className="flex items-start gap-3">
                                        <MapPin
                                            size={16}
                                            className="text-gold mt-0.5 shrink-0"
                                        />
                                        <span className="text-cream/85">{b.address}</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Phone
                                            size={16}
                                            className="text-gold mt-0.5 shrink-0"
                                        />
                                        <a
                                            href={telLink(b.phoneRaw)}
                                            className="text-cream/85 hover:text-gold-light transition-colors"
                                            data-testid={`location-phone-${b.id}`}
                                        >
                                            {b.phone}
                                        </a>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Clock
                                            size={16}
                                            className="text-gold mt-0.5 shrink-0"
                                        />
                                        <span className="text-cream/85">{b.hours}</span>
                                    </div>
                                </div>

                                <div className="mt-7 flex flex-wrap gap-2">
                                    <a
                                        href={waLink(
                                            b.whatsapp,
                                            `Hi, I would like to book at the ${b.name} branch.`
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`location-${b.id}-whatsapp`}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white rounded-sm text-xs font-medium hover:bg-[#1ebf5a] transition tracking-wide"
                                    >
                                        <MessageCircle size={14} /> WhatsApp
                                    </a>
                                    <a
                                        href={b.mapsLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`location-${b.id}-directions`}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 border border-gold/50 text-gold-light rounded-sm text-xs font-medium hover:bg-gold/15 transition tracking-wide"
                                    >
                                        <ExternalLink size={14} /> Directions
                                    </a>
                                </div>
                            </div>

                            <div className="aspect-[16/10] bg-beige border-t border-gold/20">
                                <iframe
                                    src={b.mapsEmbed}
                                    title={`Map of ${b.name} branch`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    data-testid={`location-map-${b.id}`}
                                />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
